import { type FC, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Modal } from '@/components/ui/modal'
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalXCloseButton
} from '@/components/ui/modal-atoms'
import { Spinner } from '@/components/ui/spinner'
import { useGetGalleryImages } from '@/features/service-vetting/query/use-get-gallery-images.ts'
import { useSubmitService } from '@/features/service-vetting/query/use-submit-service.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import type { RemappedService } from '@/features/service-vetting/types.ts'
import { useTranslation } from '@/translations/i18n'

interface AdminServiceVettingConfirmationModalProps {
	onClose: () => void
	service: RemappedService
	isWaitlisted: boolean
}
const MINIMUM_REQUIRED_PHOTOS = 1
export const ServiceVettingSubmitModal: FC<
	AdminServiceVettingConfirmationModalProps
> = ({ service, onClose, isWaitlisted }) => {
	const { t } = useTranslation()
	const resetErrors = useServiceVettingError((state) => state.reset)
	const submitQuery = useSubmitService(
		{ serviceId: service.id },
		{
			onSettled: () => onClose(),
			onSuccess: () => {
				toast.success(getToastMessage())
				resetErrors()
			}
		}
	)
	const imagesQuery = useGetGalleryImages({
		serviceId: service.id,
		galleryId: service.gallery.id
	})

	const galleryImages = useMemo(
		() => imagesQuery.data?.filter((img) => !img.is_offering_image) || [],
		[imagesQuery.data]
	)
	const validPhotosCount = useMemo(
		() => galleryImages.filter((img) => img.is_approved).length,
		[galleryImages]
	)
	const isDeclined = useMemo(
		() => service.expertise.status === 'declined',
		[service]
	)
	const hasBackToDraftExpertise = service.expertise.status === 'back_to_draft'
	const hasDeclinedOrBackToDraftOfferings = service.offerings.some(
		(offering) =>
			offering.status === 'declined' ||
			offering.status === 'back_to_draft'
	)
	const hasInsufficientPhotos = validPhotosCount < MINIMUM_REQUIRED_PHOTOS

	const getSubtitleByService = useCallback(() => {
		// Early return if expertise is declined
		if (isDeclined) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submission_rejected'
			)
		}

		// Check if service should go back to draft
		if (
			hasBackToDraftExpertise ||
			hasDeclinedOrBackToDraftOfferings ||
			hasInsufficientPhotos
		) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submission_back_to_draft'
			)
		}

		// Default case: service is accepted
		return t(
			'vetting:criteria.commons.confirmation_modal.submission_accepted'
		)
	}, [
		hasBackToDraftExpertise,
		hasDeclinedOrBackToDraftOfferings,
		hasInsufficientPhotos,
		isDeclined,
		t
	])

	const getToastMessage = () => {
		if (isDeclined) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submit_rejected_toast'
			)
		}
		if (
			hasBackToDraftExpertise ||
			hasDeclinedOrBackToDraftOfferings ||
			hasInsufficientPhotos
		) {
			return t(
				'vetting:criteria.commons.confirmation_modal.submit_back_to_draft_toast'
			)
		}
		return t(
			'vetting:criteria.commons.confirmation_modal.submit_accepted_toast'
		)
	}

	return (
		<Modal width={480}>
			<ModalHeader className='grid grid-cols-[36px_minmax(0,1fr)_36px] items-center justify-items-center border-neutral-300 border-b'>
				<ModalXCloseButton onClick={onClose} />
				<p className='text-neutral-900'>
					{t('vetting:criteria.commons.confirmation_modal.title')}
				</p>
			</ModalHeader>
			<ModalBody className='gap-1'>
				<h3 className='font-medium text-neutral-900 text-xl'>
					{getSubtitleByService()}
				</h3>
				<p className='text-neutral-900'>
					{t(
						'vetting:criteria.commons.confirmation_modal.description'
					)}
				</p>
			</ModalBody>
			<ModalFooter>
				<ButtonLegacy
					onClick={onClose}
					type='button'
					variant={'tertiary'}
				>
					{t('common:cancel')}
				</ButtonLegacy>

				<ButtonBrandLegacy
					disabled={submitQuery.isPending}
					onClick={() =>
						submitQuery.mutate({
							is_waitlisted: isWaitlisted,
							is_host_approval_needed: false
						})
					}
				>
					{t('common:confirm')}
					{submitQuery.isPending && <Spinner size={20} />}
				</ButtonBrandLegacy>
			</ModalFooter>
		</Modal>
	)
}
